import { Cache } from 'memory-cache';
import { type MailboxInfo } from 'owa-client-types';
import { getIdentityKeyForMailboxInfo } from 'owa-auth-types';
import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { EnterpriseTokenRequestParams, TokenRequestParams } from 'owa-tokenprovider-utils';
import {
    MIN_TIME_TO_EXPIRY_IN_MINUTES,
    TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS,
    ScenarioType,
} from 'owa-tokenprovider-utils';
import type { ITokenCache } from './ITokenCache';
import addMinutes from 'owa-date-utc-fn/lib/addMinutes';
import type TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import addSeconds from 'owa-date-utc-fn/lib/addSeconds';
import { isBootFeatureEnabled } from 'owa-metatags';

export class TokenCache implements ITokenCache {
    private tokenCache: Cache<string, TokenResponse>;

    public constructor() {
        this.tokenCache = new Cache();
    }

    /* getCachedToken gets token from tokenCache for Enterprise, Consumer and Gmail scenarios
     */
    public getCachedToken(cacheKey: string): TokenResponse | undefined {
        const cachedToken: TokenResponse = this.tokenCache.get(cacheKey) as TokenResponse;
        let tokenResponse: TokenResponse | undefined = undefined;

        if (cachedToken?.AccessTokenExpiry && cachedToken.AccessToken) {
            const expiryDateTimeWithMargin = addMinutes(
                new Date(cachedToken.AccessTokenExpiry),
                -MIN_TIME_TO_EXPIRY_IN_MINUTES
            );

            // Check that token is valid for at least 1 minute.
            if (expiryDateTimeWithMargin > new Date(Date.now())) {
                tokenResponse = cachedToken;
            }
        }

        return tokenResponse;
    }

    /* putCachedToken populates token to tokenCache for Enterprise, Consumer and Gmail scenarios
       It also takes a callback (cacheTimeoutCallback) method to be called when token has expired
    */
    public putCachedToken(
        cacheKey: string,
        requestParams: TokenRequestParams,
        tokenResponse: TokenResponse,
        cacheTimeoutCallback?: () => Promise<TokenResponse | undefined>
    ): void {
        if (
            tokenResponse &&
            tokenResponse.TokenResultCode == 0 &&
            tokenResponse.AccessTokenExpiry
        ) {
            if (
                requestParams.scenarioType == ScenarioType.Enterprise &&
                tokenResponse.ExpiresIn &&
                tokenResponse.ExpiresIn > 0
            ) {
                const tokenExpiryDate = tokenResponse.AdditionalData?.get('tokenExpiryDate');
                if (tokenExpiryDate && isBootFeatureEnabled('auth-tokenCache-expiryDate')) {
                    tokenResponse.AccessTokenExpiry = tokenExpiryDate + 'Z'; //nh expiry date is in UTC
                } else {
                    tokenResponse.AccessTokenExpiry = addSeconds(
                        Date.now(),
                        tokenResponse.ExpiresIn
                    ).toISOString();
                }
            }

            const cacheTimeout: number =
                new Date(tokenResponse.AccessTokenExpiry).getTime() -
                Date.now() -
                TIME_BEFORE_EXPIRY_TO_REFRESH_IN_MILLISECONDS;

            if (cacheTimeout > 0) {
                this.tokenCache.put(cacheKey, tokenResponse, cacheTimeout, cacheTimeoutCallback);
            }
        }
    }

    /* GetCacheKey currently takes into account all the request
        params and gets back the key to be used for all of Enterprise,
        Consumer & Gmail scenarios
    */
    public getCacheKey(params: TokenRequestParams): string {
        let key: string = params.resource ?? '';

        if (params.scenarioType == ScenarioType.Enterprise) {
            const enterpriseParams = params as EnterpriseTokenRequestParams;
            if (enterpriseParams.targetTenantId) {
                key += '|' + enterpriseParams.targetTenantId;
            }

            if (enterpriseParams.preferIdpToken) {
                key += '|IdpToken';
            }
        }

        const identityKey = getIdentityKeyForMailboxInfo(params.mailboxInfo);
        if (identityKey) {
            key += '|' + identityKey;
        }

        if (params.scope) {
            key += '|' + params.scope;
        }

        return key;
    }

    /* deleteCachedToken deletes token from tokenCache for Enterprise, Consumer and Gmail scenarios
     */
    public deleteCachedToken(cacheKey: string): void {
        this.tokenCache.del(cacheKey);
    }

    /* getAllCacheKeys returns all the keys in the tokenCache
     */
    public getAllCacheKeys(): string[] {
        return this.tokenCache.keys();
    }

    /* clears out all the cache key that corresponds to this mailboxInfo
     */
    public clearCacheForMailboxInfo(mailboxInfo?: MailboxInfo): void {
        const identityKey = getIdentityKeyForMailboxInfo(mailboxInfo);
        if (identityKey) {
            const cacheKeys = this.getAllCacheKeys();
            cacheKeys.forEach(key => {
                if (key.includes(identityKey)) {
                    this.deleteCachedToken(key);
                }
            });
        }
    }
}
